<template>
  <div class="feature-icon" :class="[sizeClass, variantClass]" v-html="iconSvg"></div>
</template>

<script setup>
const props = defineProps({
  name: {
    type: String,
    required: true
  },
  size: {
    type: String,
    default: 'md',
    validator: (value) => ['sm', 'md', 'lg', 'xl', '2xl', '3xl'].includes(value)
  },
  variant: {
    type: String,
    default: 'gradient',
    validator: (value) => ['gradient', 'solid', 'outline', 'minimal'].includes(value)
  }
})

const sizeClass = computed(() => {
  const sizes = {
    sm: 'w-5 h-5',
    md: 'w-6 h-6',
    lg: 'w-8 h-8',
    xl: 'w-12 h-12',
    '2xl': 'w-16 h-16',
    '3xl': 'w-24 h-24'
  }
  return sizes[props.size] || sizes.md
})

const variantClass = computed(() => {
  const variants = {
    gradient: 'feature-icon-gradient',
    solid: 'feature-icon-solid',
    outline: 'feature-icon-outline',
    minimal: 'feature-icon-minimal'
  }
  return variants[props.variant] || variants.gradient
})

const icons = {
  // Core product icons (upgraded)
  ssp: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="4" width="18" height="16" rx="4" stroke="currentColor" stroke-width="1.9"/>
    <path d="M7 9H17" stroke="currentColor" stroke-width="1.9"/>
    <path d="M7 13H13" stroke="currentColor" stroke-width="1.9"/>
    <circle cx="16.5" cy="13" r="1.5" fill="currentColor" opacity="0.3"/>
    <path d="M7 17H11" stroke="currentColor" stroke-width="1.9"/>
  </svg>`,
  dsp: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="8.5" stroke="currentColor" stroke-width="1.9"/>
    <circle cx="12" cy="12" r="4.5" stroke="currentColor" stroke-width="1.9" opacity="0.9"/>
    <circle cx="12" cy="12" r="1.8" fill="currentColor"/>
    <path d="M12 3V1.8M12 22.2V21M3 12H1.8M22.2 12H21" stroke="currentColor" stroke-width="1.8"/>
    <path d="M12 12L17.5 8.8" stroke="currentColor" stroke-width="1.9"/>
    <circle cx="17.5" cy="8.8" r="1.8" fill="currentColor" opacity="0.2" stroke="currentColor" stroke-width="1.1"/>
  </svg>`,
  sdk: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="6" y="2.5" width="12" height="19" rx="3" stroke="currentColor" stroke-width="1.9"/>
    <path d="M9.6 10L8.1 12L9.6 14" stroke="currentColor" stroke-width="1.9"/>
    <path d="M14.4 10L15.9 12L14.4 14" stroke="currentColor" stroke-width="1.9"/>
    <path d="M11.3 16.5H12.7" stroke="currentColor" stroke-width="1.9"/>
  </svg>`,
  adx: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="9.5" stroke="currentColor" stroke-width="1.9"/>
    <path d="M2.5 12H21.5" stroke="currentColor" stroke-width="1.9"/>
    <path d="M12 2.5C14.8 5.1 16.5 8.4 16.5 12C16.5 15.6 14.8 18.9 12 21.5" stroke="currentColor" stroke-width="1.9"/>
    <path d="M12 2.5C9.2 5.1 7.5 8.4 7.5 12C7.5 15.6 9.2 18.9 12 21.5" stroke="currentColor" stroke-width="1.9" opacity="0.8"/>
  </svg>`,
  account: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M13.5 2.5L5 13H10.5L9.6 21.5L19 10.7H13.4L13.5 2.5Z" stroke="currentColor" stroke-width="1.9"/>
  </svg>`,
  'private-domain': `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2L19 5V10.2C19 15.1 16 19.4 12 21.2C8 19.4 5 15.1 5 10.2V5L12 2Z" stroke="currentColor" stroke-width="1.9"/>
    <path d="M12 7.2L13.6 10.1L16.7 10.6L14.5 12.8L15 15.9L12 14.3L9 15.9L9.5 12.8L7.3 10.6L10.4 10.1L12 7.2Z" fill="currentColor" opacity="0.24"/>
  </svg>`,

  // Feature icons (upgraded)
  targeting: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="8" stroke="currentColor" stroke-width="1.9"/>
    <circle cx="12" cy="12" r="4.3" stroke="currentColor" stroke-width="1.9" opacity="0.9"/>
    <circle cx="12" cy="12" r="1.8" fill="currentColor"/>
    <path d="M12 2V4M12 20V22M2 12H4M20 12H22" stroke="currentColor" stroke-width="1.9"/>
  </svg>`,
  ai: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="5" y="5" width="14" height="14" rx="3.2" stroke="currentColor" stroke-width="1.9"/>
    <path d="M9 15V9H11.2C12.2 9 13 9.8 13 10.8C13 11.8 12.2 12.6 11.2 12.6H9" stroke="currentColor" stroke-width="1.9"/>
    <path d="M15 9V15M15 12H18" stroke="currentColor" stroke-width="1.9"/>
    <path d="M9 2.6V4M15 2.6V4M20 9H21.4M20 15H21.4M9 20V21.4M15 20V21.4M2.6 9H4M2.6 15H4" stroke="currentColor" stroke-width="1.7" opacity="0.75"/>
  </svg>`,
  monitor: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="3.5" width="18" height="11.5" rx="2.2" stroke="currentColor" stroke-width="1.9"/>
    <path d="M12 15V20.5M8.5 20.5H15.5" stroke="currentColor" stroke-width="1.9"/>
  </svg>`,
  'cross-screen': `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2.5" y="5" width="11" height="13" rx="2" stroke="currentColor" stroke-width="1.9"/>
    <rect x="11.5" y="7" width="10" height="9" rx="1.8" stroke="currentColor" stroke-width="1.9"/>
  </svg>`,
  analytics: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 19.5V13.6M9.3 19.5V8.8M14.7 19.5V11.2M20 19.5V5.5" stroke="currentColor" stroke-width="1.9"/>
    <path d="M3 19.5H21" stroke="currentColor" stroke-width="1.9" opacity="0.65"/>
  </svg>`,
  budget: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2.5" y="6.2" width="19" height="11.6" rx="2.5" stroke="currentColor" stroke-width="1.9"/>
    <circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="1.9"/>
    <path d="M2.5 9.5H5.5M18.5 14.5H21.5" stroke="currentColor" stroke-width="1.9" opacity="0.75"/>
  </svg>`,
  rtb: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M13.5 2.5L5 13H10.5L9.6 21.5L19 10.7H13.4L13.5 2.5Z" stroke="currentColor" stroke-width="1.9"/>
    <path d="M2.5 8.5H5M2 12H4.5M2.5 15.5H5" stroke="currentColor" stroke-width="1.6" opacity="0.72"/>
  </svg>`,
  pricing: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2.5L3 7L12 11.5L21 7L12 2.5Z" stroke="currentColor" stroke-width="1.9"/>
    <path d="M3 12.5L12 17L21 12.5" stroke="currentColor" stroke-width="1.9"/>
    <path d="M3 17.2L12 21.5L21 17.2" stroke="currentColor" stroke-width="1.9" opacity="0.85"/>
  </svg>`,
  data: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <ellipse cx="12" cy="5.5" rx="7.3" ry="2.5" stroke="currentColor" stroke-width="1.9"/>
    <path d="M4.7 5.5V12.5C4.7 13.9 7.9 15.1 12 15.1C16.1 15.1 19.3 13.9 19.3 12.5V5.5" stroke="currentColor" stroke-width="1.9"/>
    <path d="M4.7 12.3V18.5C4.7 19.9 7.9 21.1 12 21.1C16.1 21.1 19.3 19.9 19.3 18.5V12.3" stroke="currentColor" stroke-width="1.9" opacity="0.85"/>
  </svg>`,
  security: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2L19 5V10.2C19 15.1 16 19.4 12 21.2C8 19.4 5 15.1 5 10.2V5L12 2Z" stroke="currentColor" stroke-width="1.9"/>
    <path d="M9.2 12.1L11.1 14L14.8 10.3" stroke="currentColor" stroke-width="1.9"/>
  </svg>`,
  global: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="9.5" stroke="currentColor" stroke-width="1.9"/>
    <path d="M2.5 12H21.5" stroke="currentColor" stroke-width="1.9" opacity="0.9"/>
    <path d="M12 2.5C14.8 5.1 16.5 8.4 16.5 12C16.5 15.6 14.8 18.9 12 21.5" stroke="currentColor" stroke-width="1.9"/>
    <path d="M12 2.5C9.2 5.1 7.5 8.4 7.5 12C7.5 15.6 9.2 18.9 12 21.5" stroke="currentColor" stroke-width="1.9" opacity="0.8"/>
  </svg>`,
  users: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="9" cy="7.5" r="3.3" stroke="currentColor" stroke-width="1.9"/>
    <path d="M2.8 19.5C2.8 16.8 5.3 14.8 9 14.8C12.7 14.8 15.2 16.8 15.2 19.5" stroke="currentColor" stroke-width="1.9"/>
    <path d="M16.4 15.2C18.9 15.5 20.8 17.2 21.2 19.5" stroke="currentColor" stroke-width="1.9" opacity="0.75"/>
    <path d="M16.2 4.6C17.8 5 19 6.4 19 8C19 9.6 17.8 11 16.2 11.4" stroke="currentColor" stroke-width="1.9" opacity="0.75"/>
  </svg>`,
  integration: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="4" y="4" width="6" height="6" rx="1.6" stroke="currentColor" stroke-width="1.9"/>
    <rect x="14" y="4" width="6" height="6" rx="1.6" stroke="currentColor" stroke-width="1.9"/>
    <rect x="4" y="14" width="6" height="6" rx="1.6" stroke="currentColor" stroke-width="1.9"/>
    <rect x="14" y="14" width="6" height="6" rx="1.6" stroke="currentColor" stroke-width="1.9"/>
    <path d="M10 7H14M7 10V14M17 10V14M10 17H14" stroke="currentColor" stroke-width="1.9"/>
  </svg>`,
  speed: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M13.5 2.5L5 13H10.5L9.6 21.5L19 10.7H13.4L13.5 2.5Z" stroke="currentColor" stroke-width="1.9"/>
    <path d="M2.5 8.7H5.3M2 12.2H4.8M2.5 15.7H5.3" stroke="currentColor" stroke-width="1.7" opacity="0.76"/>
  </svg>`,
  growth: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 19.5H20" stroke="currentColor" stroke-width="1.9"/>
    <path d="M6.2 16.2L10 12.4L13 14.8L18 9.8" stroke="currentColor" stroke-width="1.9"/>
    <path d="M15.8 9.8H18V12" stroke="currentColor" stroke-width="1.9"/>
  </svg>`,
  rocket: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14.8 4.1C17.1 3.2 19.3 3.3 21 3.6C20.7 5.3 20.6 7.5 19.7 9.8C18.5 12.9 15.9 15.4 12.8 16.7L10.9 14.8C12.1 11.7 13.6 7.3 14.8 4.1Z" stroke="currentColor" stroke-width="1.9"/>
    <circle cx="16.4" cy="7.3" r="1.3" fill="currentColor" opacity="0.24" stroke="currentColor" stroke-width="1"/>
    <path d="M10.9 14.8L7.8 17.9C7 18.7 5.7 18.8 4.8 18.1L3.4 16.8C2.7 15.9 2.8 14.6 3.6 13.8L6.7 10.7" stroke="currentColor" stroke-width="1.9"/>
    <path d="M10.2 19.8C10.2 19.8 10.3 17.3 12.6 15" stroke="currentColor" stroke-width="1.9"/>
  </svg>`,

  // Utility icons
  vision: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2.5 12C4.2 8.8 7.5 6.2 12 6.2C16.5 6.2 19.8 8.8 21.5 12C19.8 15.2 16.5 17.8 12 17.8C7.5 17.8 4.2 15.2 2.5 12Z" stroke="currentColor" stroke-width="1.9"/>
    <circle cx="12" cy="12" r="2.9" stroke="currentColor" stroke-width="1.9"/>
  </svg>`,
  gem: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3.5 8.2L8 3.5H16L20.5 8.2L12 20.5L3.5 8.2Z" stroke="currentColor" stroke-width="1.9"/>
    <path d="M8 3.5L12 8.2L16 3.5M3.5 8.2H20.5" stroke="currentColor" stroke-width="1.9" opacity="0.8"/>
  </svg>`,
  user: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="8" r="3.6" stroke="currentColor" stroke-width="1.9"/>
    <path d="M4 20C4 16.8 7.1 14.8 12 14.8C16.9 14.8 20 16.8 20 20" stroke="currentColor" stroke-width="1.9"/>
  </svg>`,
  mobile: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="6" y="2.5" width="12" height="19" rx="3" stroke="currentColor" stroke-width="1.9"/>
    <path d="M10.7 18H13.3" stroke="currentColor" stroke-width="1.9"/>
  </svg>`,
  email: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="5" width="18" height="14" rx="2.2" stroke="currentColor" stroke-width="1.9"/>
    <path d="M3.8 6L12 12.5L20.2 6" stroke="currentColor" stroke-width="1.9"/>
  </svg>`,
  location: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2.5C7.9 2.5 4.5 5.8 4.5 9.9C4.5 15.4 12 21.3 12 21.3C12 21.3 19.5 15.4 19.5 9.9C19.5 5.8 16.1 2.5 12 2.5Z" stroke="currentColor" stroke-width="1.9"/>
    <circle cx="12" cy="9.8" r="2.7" stroke="currentColor" stroke-width="1.9"/>
  </svg>`,
  gaming: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2.7" y="7" width="18.6" height="10" rx="4" stroke="currentColor" stroke-width="1.9"/>
    <path d="M8.3 12H12.1M10.2 10.1V13.9" stroke="currentColor" stroke-width="1.9"/>
    <circle cx="16.6" cy="10.8" r="1.15" fill="currentColor" opacity="0.28" stroke="currentColor" stroke-width="1"/>
    <circle cx="18.4" cy="13.2" r="1.15" fill="currentColor" opacity="0.28" stroke="currentColor" stroke-width="1"/>
  </svg>`,
  shopping: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M7 9V7.2C7 4.9 8.9 3 11.2 3H12.8C15.1 3 17 4.9 17 7.2V9" stroke="currentColor" stroke-width="1.9"/>
    <rect x="4" y="8" width="16" height="12.5" rx="2.2" stroke="currentColor" stroke-width="1.9"/>
    <path d="M9.2 12H14.8" stroke="currentColor" stroke-width="1.9"/>
  </svg>`,
  finance: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3.5 19.5H20.5" stroke="currentColor" stroke-width="1.9"/>
    <path d="M6 19.5V11.5M12 19.5V8.5M18 19.5V5.5" stroke="currentColor" stroke-width="1.9"/>
    <path d="M5 6.5L8.8 5L12 7.8L18.5 4" stroke="currentColor" stroke-width="1.9"/>
  </svg>`,
  money: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <ellipse cx="12" cy="6" rx="6.8" ry="2.5" stroke="currentColor" stroke-width="1.9"/>
    <path d="M5.2 6V13.5C5.2 14.9 8.2 16.1 12 16.1C15.8 16.1 18.8 14.9 18.8 13.5V6" stroke="currentColor" stroke-width="1.9"/>
    <path d="M12 16.1V21" stroke="currentColor" stroke-width="1.9"/>
  </svg>`,
  phone: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M20.7 16.4V18.8C20.7 19.9 19.8 20.8 18.7 20.7C14.8 20.4 11.3 18.9 8.5 16.1C5.7 13.3 4.2 9.8 3.9 5.9C3.8 4.8 4.7 3.9 5.8 3.9H8.2C9.1 3.9 9.9 4.5 10.1 5.4L10.8 8.3C10.9 8.9 10.7 9.6 10.2 10L8.6 11.3C9.8 13.4 11.6 15.2 13.7 16.4L15 14.8C15.4 14.3 16.1 14.1 16.7 14.2L19.6 14.9C20.4 15.1 20.7 15.6 20.7 16.4Z" stroke="currentColor" stroke-width="1.9"/>
  </svg>`,
  document: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M7 2.8H14L19.5 8.3V20.2C19.5 21.1 18.8 21.8 17.9 21.8H7C6.1 21.8 5.4 21.1 5.4 20.2V4.4C5.4 3.5 6.1 2.8 7 2.8Z" stroke="currentColor" stroke-width="1.9"/>
    <path d="M14 2.8V8.3H19.5M8.5 12H16.2M8.5 15.5H16.2" stroke="currentColor" stroke-width="1.9"/>
  </svg>`,
  chat: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M5 5.8H19C20.1 5.8 21 6.7 21 7.8V15.3C21 16.4 20.1 17.3 19 17.3H11.2L6 20.7V17.3H5C3.9 17.3 3 16.4 3 15.3V7.8C3 6.7 3.9 5.8 5 5.8Z" stroke="currentColor" stroke-width="1.9"/>
    <path d="M7.8 10.2H16.2M7.8 13.2H13.6" stroke="currentColor" stroke-width="1.9"/>
  </svg>`,
  time: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="9.5" stroke="currentColor" stroke-width="1.9"/>
    <path d="M12 6.5V12L15.8 14.2" stroke="currentColor" stroke-width="1.9"/>
  </svg>`,
  'shield-check': `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2L19 5V10.2C19 15.1 16 19.4 12 21.2C8 19.4 5 15.1 5 10.2V5L12 2Z" stroke="currentColor" stroke-width="1.9"/>
    <path d="M9.2 12.1L11.1 14L14.8 10.3" stroke="currentColor" stroke-width="1.9"/>
  </svg>`,
  handshake: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8.5 11.8L11.2 14.5C12 15.3 13.2 15.3 14 14.5L17.4 11.1C18.2 10.3 18.2 9 17.4 8.2L15.8 6.6C15 5.8 13.7 5.8 12.9 6.6L10.6 8.9" stroke="currentColor" stroke-width="1.9"/>
    <path d="M8.5 11.8L6.6 13.7C5.8 14.5 4.5 14.5 3.7 13.7L2.8 12.8C2 12 2 10.7 2.8 9.9L6.5 6.2C7.3 5.4 8.6 5.4 9.4 6.2L10.6 7.4" stroke="currentColor" stroke-width="1.9"/>
    <path d="M17.6 11L20.2 13.6C21 14.4 21 15.7 20.2 16.5L19.4 17.3C18.6 18.1 17.3 18.1 16.5 17.3L14.4 15.2" stroke="currentColor" stroke-width="1.9"/>
  </svg>`,
  lightbulb: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9 18H15" stroke="currentColor" stroke-width="1.9"/>
    <path d="M10 21H14" stroke="currentColor" stroke-width="1.9"/>
    <path d="M8 10.3C8 7.8 10 5.8 12.5 5.8C15 5.8 17 7.8 17 10.3C17 12 16.2 13.3 15 14.3C14.3 14.9 14 15.5 14 16.3V18H10.8V16.3C10.8 15.5 10.4 14.9 9.8 14.3C8.6 13.3 8 12 8 10.3Z" stroke="currentColor" stroke-width="1.9"/>
    <path d="M12 2.6V4M5.6 6.2L6.7 7.3M18.4 6.2L17.3 7.3" stroke="currentColor" stroke-width="1.7" opacity="0.76"/>
  </svg>`,
  search: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="10.8" cy="10.8" r="6.8" stroke="currentColor" stroke-width="1.9"/>
    <path d="M15.7 15.7L20.5 20.5" stroke="currentColor" stroke-width="1.9"/>
  </svg>`,
  education: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 3L2.5 7.5L12 12L21.5 7.5L12 3Z" stroke="currentColor" stroke-width="1.9"/>
    <path d="M6 10.3V15.8C6 17.6 8.7 19 12 19C15.3 19 18 17.6 18 15.8V10.3" stroke="currentColor" stroke-width="1.9"/>
  </svg>`,
  health: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="9.5" stroke="currentColor" stroke-width="1.9"/>
    <path d="M8 12H16M12 8V16" stroke="currentColor" stroke-width="1.9"/>
  </svg>`,
  automotive: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M5.2 16.8H3.5C2.9 16.8 2.5 16.4 2.5 15.8V12.5L4.4 8.7C4.7 8 5.4 7.5 6.2 7.5H17.8C18.6 7.5 19.3 8 19.6 8.7L21.5 12.5V15.8C21.5 16.4 21.1 16.8 20.5 16.8H18.8" stroke="currentColor" stroke-width="1.9"/>
    <circle cx="7.2" cy="16.8" r="2.2" stroke="currentColor" stroke-width="1.9"/>
    <circle cx="16.8" cy="16.8" r="2.2" stroke="currentColor" stroke-width="1.9"/>
  </svg>`,
  lifecycle: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="8.8" stroke="currentColor" stroke-width="1.9"/>
    <path d="M12 6.4V12L16 14.3" stroke="currentColor" stroke-width="1.9"/>
    <path d="M19.2 8.2L20.7 8.4L20.5 6.8" stroke="currentColor" stroke-width="1.7" opacity="0.76"/>
  </svg>`,
  member: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="7.5" r="3.2" stroke="currentColor" stroke-width="1.9"/>
    <path d="M5.4 20C5.4 16.8 8.4 14.8 12 14.8C15.6 14.8 18.6 16.8 18.6 20" stroke="currentColor" stroke-width="1.9"/>
    <path d="M4.8 4.6C6.4 3.3 8.7 2.5 12 2.5C15.3 2.5 17.6 3.3 19.2 4.6" stroke="currentColor" stroke-width="1.7" opacity="0.72"/>
  </svg>`,
  'customer-service': `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4.2 12C4.2 7.6 7.8 4 12.2 4C16.6 4 20.2 7.6 20.2 12" stroke="currentColor" stroke-width="1.9"/>
    <rect x="3" y="11.2" width="3.6" height="5.6" rx="1.5" stroke="currentColor" stroke-width="1.9"/>
    <rect x="17.8" y="11.2" width="3.2" height="5.6" rx="1.5" stroke="currentColor" stroke-width="1.9"/>
    <path d="M10.6 17.8H13.2" stroke="currentColor" stroke-width="1.9"/>
  </svg>`
}

const iconSvg = computed(() => {
  return icons[props.name] || icons.targeting
})
</script>

<style scoped>
.feature-icon {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.28s ease, filter 0.28s ease;
}

.feature-icon::before {
  content: '';
  position: absolute;
  inset: -22%;
  border-radius: 9999px;
  background: radial-gradient(circle, rgba(99, 102, 241, 0.26), transparent 70%);
  opacity: 0;
  transform: scale(0.9);
  transition: opacity 0.28s ease, transform 0.28s ease;
  pointer-events: none;
}

.feature-icon svg {
  width: 100%;
  height: 100%;
}

.feature-icon svg * {
  stroke-linecap: round;
  stroke-linejoin: round;
  vector-effect: non-scaling-stroke;
}

.feature-icon-gradient {
  color: #1d4ed8;
  padding: 0.34rem;
  border-radius: 0.92rem;
  border: 1px solid rgba(59, 130, 246, 0.32);
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.84), rgba(219, 234, 254, 0.72));
  box-shadow: 0 12px 26px rgba(59, 130, 246, 0.16), inset 0 1px 0 rgba(255, 255, 255, 0.95);
}

.feature-icon-gradient svg {
  filter: drop-shadow(0 2px 8px rgba(59, 130, 246, 0.18));
}

.dark .feature-icon-gradient {
  color: #dbeafe;
  border-color: rgba(125, 211, 252, 0.42);
  background: linear-gradient(145deg, rgba(37, 99, 235, 0.38), rgba(30, 58, 138, 0.44));
  box-shadow: 0 12px 24px rgba(2, 6, 23, 0.32), inset 0 1px 0 rgba(191, 219, 254, 0.32);
}

.dark .feature-icon-gradient svg {
  filter: drop-shadow(0 2px 12px rgba(147, 197, 253, 0.44));
}

.feature-icon-solid {
  color: #1e40af;
  padding: 0.28rem;
  border-radius: 0.82rem;
  border: 1px solid rgba(59, 130, 246, 0.26);
  background: linear-gradient(145deg, rgba(219, 234, 254, 0.9), rgba(191, 219, 254, 0.72));
  box-shadow: 0 10px 20px rgba(59, 130, 246, 0.14);
}

.dark .feature-icon-solid {
  color: #e2e8f0;
  border-color: rgba(125, 211, 252, 0.42);
  background: linear-gradient(145deg, rgba(59, 130, 246, 0.34), rgba(30, 58, 138, 0.42));
  box-shadow: 0 10px 20px rgba(2, 6, 23, 0.3);
}

.feature-icon-outline {
  color: #475569;
  padding: 0.28rem;
  border-radius: 0.82rem;
  border: 1px solid rgba(71, 85, 105, 0.3);
  background: rgba(255, 255, 255, 0.56);
}

.dark .feature-icon-outline {
  color: #dbeafe;
  border-color: rgba(148, 163, 184, 0.48);
  background: rgba(30, 58, 138, 0.34);
}

.feature-icon-minimal {
  color: #1d4ed8;
  padding: 0;
  border: none;
  background: transparent;
  box-shadow: none;
}

body.dark .feature-icon-minimal {
  color: #dbeafe;
}

.feature-icon:hover {
  transform: translateY(-2px) scale(1.05);
}

.feature-icon:hover::before {
  opacity: 1;
  transform: scale(1);
}
</style>
